import { INavLink } from "core-integration/src/hooks/types";
import { RootState } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import {
  getCurrentEvent,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getServerError } from "core-integration/src/store/global/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import i18n from "../../i18n";
import ListItem from "./ListItem";
import { NavContainer, StyledNav, StyledUL } from "./styles";

interface NavigationLinksProps {
  links: INavLink[];
}

const NavigationLinks = ({ links }: NavigationLinksProps) => {
  return (
    <StyledNav role="navigation" className="ism-nav">
      <NavContainer>
        <StyledUL>
          {links.map((link) => (
            <ListItem {...link} key={link.text} />
          ))}
        </StyledUL>
      </NavContainer>
    </StyledNav>
  );
};

const Navigation = () => {
  const { t } = useTranslation();

  const player = useSelector(getPlayerData);
  const currentEvent = useSelector(getCurrentEvent);
  const nextEvent = useSelector(getNextEvent);
  const serverError = useSelector(getServerError);

  const entry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );

  let links: INavLink[] = [];
  const prizeExclusionCountries: string[] = ["ar", "he", "it", "tr"];
  const useRouter = !serverError;

  if (player?.entry) {
    // Logged in with entry
    links = [
      {
        useRouter,
        href: "/",
        text: currentEvent
          ? t("navigation.status", "Status")
          : t("navigation.home", "Home"),
      },
    ];
    if (nextEvent) {
      links.push({
        useRouter,
        href: "/my-team",
        text: t("navigation.myTeamN", "Edit line-up"),
      });
    }
    // If the entry has started ...
    if (currentEvent && entry && entry.started_event <= currentEvent.id) {
      links.push({
        useRouter,
        href: `/entry/${player.entry}/event/${currentEvent.id}`,
        text: t("navigation.points", "Points"),
      });
    }
    links.push(
      {
        useRouter,
        href: "/transfers",
        text: t("navigation.transfers", "Transactions"),
      },
      {
        useRouter,
        href: "/leagues",
        text: t("navigation.leagues", "Leagues"),
      },
      {
        useRouter,
        href: "/fixtures",
        text: t("navigation.fixturesN", "Schedule"),
      },
      {
        useRouter,
        href: "/statistics",
        text: t("navigation.statistics", "Statistics"),
      },
      {
        useRouter,
        href: "/player-news",
        text: t("navigation.playerNews", "Player News"),
      },
      {
        useRouter,
        href: "/prizes",
        text: t("navigation.prizes", "Prizes"),
      },
      {
        useRouter,
        href: "/help/rules",
        text: t("navigation.help", "Help"),
      },
      {
        useRouter,
        href: "/player/logout",
        text: t("navigation.signOut", "Sign out"),
      },
    );
    if (prizeExclusionCountries.includes(i18n.language)) {
      links = links.filter((link) => link.href !== "/prizes");
    }
  } else if (player) {
    // Logged in, no entry
    links = [
      {
        useRouter,
        href: "/",
        text: t("navigation.home", "Home"),
      },
      {
        useRouter,
        href: "/help/rules",
        text: t("navigation.help", "Help"),
      },
      {
        useRouter,
        href: "/player/logout",
        text: t("navigation.signOut", "Sign out"),
      },
    ];
    if (!nextEvent) {
      links.splice(1, 0, {
        useRouter,
        href: "/update",
        text: t("navigation.update", "Update"),
      });
    } else {
      links.splice(1, 0, {
        useRouter,
        href: "/squad-selection",
        text: t("navigation.squadSelectionN", "Roster Selection"),
      });
    }
  } else {
    // Logged Out
    links = [
      {
        useRouter,
        href: "/",
        text: t("navigation.home", "Home"),
      },
      {
        useRouter,
        href: "/help/rules",
        text: t("navigation.help", "Help"),
      },
      {
        useRouter,
        href: "/statistics",
        text: t("navigation.statistics", "Statistics"),
      },
      {
        useRouter,
        href: "/player-news",
        text: t("navigation.playerNews", "Player News"),
      },
    ];
  }
  return <NavigationLinks links={links} />;
};

export { Navigation as NavigationTest };

export default Navigation;
